import styled from "@emotion/styled"
import { css, keyframes } from '@emotion/react'
import facepaint from "facepaint"

const breakpoints = [440, 768, 1024, 1200, 1400];
const mq = facepaint(breakpoints.map(bp => `@media (min-width: ${bp}px)`));

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(32px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const wrapMQ = css(mq({
  gap: [
    0, // 0+
    0, // 440+
    0, // 768+
    0, // 1024+
    44, // 1200+
    64, // 1400+
  ],
}))

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  ${wrapMQ}
`

const listMQ = css(mq({
  gridTemplateColumns: [
    "1fr", // 0+
    "1fr", // 440+
    "1fr", // 768+
    "1fr", // 1024+
    "1fr 1fr 1fr", // 1200+
    "1fr 1fr 1fr", // 1400+
  ],
  gap: [
    0, // 0+
    0, // 440+
    0, // 768+
    0, // 1024+
    40, // 1200+
    40, // 1400+
  ],
  boxShadow: [
      "inset 0 -1px 0 rgba(0,0,0,0.1)", // 0+
      "inset 0 -1px 0 rgba(0,0,0,0.1)", // 440+
      "inset 0 -1px 0 rgba(0,0,0,0.1)", // 768+
      "inset 0 -1px 0 rgba(0,0,0,0.1)", // 1024+
      "none", // 1200+
      "none", // 1400+
  ],
}))

export const List = styled.div`
  display: grid;
  ${listMQ}
`

const activeItem = ({ isActive }) => {
  if (isActive) {
    return css(mq({
      "& button > strong": {
        font: [
          "var(--accent-570)!important", // 0+
          "var(--accent-570)!important", // 440+
          "var(--accent-570)!important", // 768+
          "var(--accent-570)!important", // 1024+
          "var(--text-670)!important", // 1200+
          "var(--text-670)!important", // 1400+
        ],
        color: "var(--color-grayscale-900)!important",
        boxShadow: [
           "none", // 0+
           "none", // 440+
           "none", // 768+
           "none", // 1024+
           "inset 0 -4px 0 var(--color-grayscale-850)", // 1200
           "inset 0 -4px 0 var(--color-grayscale-850)", // 1400+
        ],
        "& svg": {
          strokeWidth: "2px!important",
        }
      }
    }))
  }
}

const itemMQ = ({ isActive }) => {
  return css(mq({
    boxShadow: [
      "inset 0 1px 0 rgba(0,0,0,0.1)", // 0+
      "inset 0 1px 0 rgba(0,0,0,0.1)", // 440+
      "inset 0 1px 0 rgba(0,0,0,0.1)", // 768+
      "inset 0 1px 0 rgba(0,0,0,0.1)", // 1024+
      "none", // 1200+
      "none", // 1400+
    ],
    display: [
      "grid", // 0+
      "grid", // 440+
      "grid", // 768+
      "grid", // 1024+
      "flex", // 1200+
      "flex", // 1400+
    ],
    gridTemplateRows: [
      isActive ? "1fr" : "0fr", // 0+
      isActive ? "1fr" : "0fr", // 440+
      isActive ? "1fr" : "0fr", // 768+
      isActive ? "1fr" : "0fr", // 1024+
      "unset", // 1200+
      "unset", // 1400+
    ],
  }));
}

export const Item = styled.div`
  will-change: grid-template-rows;
  display: flex;
  flex-direction: column;
  grid-template-columns: 100%;
  transition: grid-template-rows .4s ease-in-out;
  text-align: left;
  overflow: hidden;
  ${activeItem}
  ${itemMQ}
`

const itemContentMQ = css(mq({
  gap: [
    0, // 0+
    0, // 440+
    0, // 768+
    0, // 1024+
    16, // 1200+
    16, // 1400+
  ],
  minHeight: [
    68, // 0+
    68, // 440+
    76, // 768+
    76, // 1024+
    "auto", // 1200+
    "auto", // 1400+
  ],
}))

export const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${itemContentMQ}
`

const itemButtonMQ = css(mq({
  gap: [
    0, // 0+
    0, // 440+
    0, // 768+
    0, // 1024+
    16, // 1200+
    16, // 1400+
  ],
  "& > strong": {
    color: [
      "var(--color-grayscale-300)", // 0+
      "var(--color-grayscale-300)", // 440+
      "var(--color-grayscale-300)", // 768+
      "var(--color-grayscale-300)", // 1024+
      "var(--color-grayscale-700)", // 1200+
      "var(--color-grayscale-700)", // 1400+
    ],
    gap: [
      16, // 0+
      16, // 440+
      16, // 768+
      16, // 1024+
      12, // 1200+
      12, // 1400+
    ],
    padding: [
      "20px 0", // 0+
      "20px 0", // 440+
      "24px 0", // 768+
      "24px 0", // 1024+
      "12px 0", // 1200+
      "12px 0", // 1400+
    ],
    font: [
      "var(--accent-570)", // 0+
      "var(--accent-570)", // 440+
      "var(--accent-570)", // 768+
      "var(--accent-570)", // 1024+
      "var(--text-640)", // 1200+
      "var(--text-640)", // 1400+
    ],
  },
}))


export const ItemButton = styled.button`
  display: flex;
  flex-direction: column;
  text-align: left;
  flex-shrink: 0;
  overflow: hidden;

  & > strong {
    display: flex;
    align-items: center;
    box-shadow: inset 0 -4px 0 transparent;
    transition: color var(--transition), box-shadow var(--transition);

    & > svg {
      width: 22px;
      height: 22px;
    }
  }

  &:hover > strong {
    color: var(--color-grayscale-900);

    & > svg {
      stroke-width: 1.2;
    }
  }

  & > p {
    font: var(--text-540);
    color: var(--color-grayscale-900);
    max-width: 540px;
  }
  ${itemButtonMQ}
`

const itemImageWrapMQ = css(mq({
  display: [
    "flex", // 0+
    "flex", // 440+
    "flex", // 768+
    "flex", // 1024+
    "none", // 1200+
    "none", // 1400+
  ],
}))

export const ItemImageWrap = styled.div`
  margin: 16px 0 19px;
  padding: 16px;
  border-radius: 16px;
  background: #4BA62E;
  flex-shrink: 0;

  & > img {
    border-radius: 12px;
    max-width: 100%;
    height: auto;
  }

  ${itemImageWrapMQ}
`

const imageOuterWrapMQ = css(mq({
  display: [
    "none", // 0+
    "none", // 440+
    "none", // 768+
    "none", // 1024+
    "flex", // 1200+
    "flex", // 1400+
  ],
}))

export const ImageOuterWrap = styled.div`
  display: flex;
  ${imageOuterWrapMQ}
`

export const ImageWrap = styled.div`
  padding: 16px;
  border-radius: 26px;
  background: #4BA62E;
  opacity: 0;
  animation: ${fadeIn} 0.6s forwards ease;

  & > img {
    border-radius: 16px;
    max-width: 100%;
    height: auto;
  }
`
